/**
 * PORTED — RO-3-00 / #44398.
 * Origin: `excellent/app/activity/activity-live.tsx` (verbatim, minus the inert
 * `"use client"`). `activity.list` is mounted on the Hono host.
 *
 * ⚠️ The `seed` prop's ORIGINAL meaning does not survive the port, and the type
 * hides it. In the Next tree `seed` was the server-rendered first paint, so the
 * live resource had rows before its first request resolved. There is no server
 * render here, so a route that passes `seed={[]}` is not "seeded" — it is
 * unseeded, and `groups` falls back to `[]` until SWR returns, which renders the
 * "all caught up" EmptyState for one frame. The honest fix belongs to whoever
 * ports `/activity` (RO-3-12): either omit the seed and gate on `isLoading`, or
 * feed it from a route loader. Do not paper over it by defaulting `seed`.
 */

import { Card, ActivityGroupCard, EmptyState } from "@excellent/ui";
import { CheckCheck } from "@excellent/ui/icons";
import { useLiveResource } from "@excellent/ui/api/live";
import {
  activity,
  type ActivityGroup,
} from "@excellent/ui/api/contracts/activity";

/**
 * The full-page Activity feed, kept live on the bus. Seeded with the
 * server-rendered groups so it paints instantly, then revalidates on every
 * `activity` topic signal (a new event from any process, a rail dismiss,
 * another tab) — so this page matches the rail without a reload.
 */
export function ActivityLive({ seed }: { seed: ActivityGroup[] }) {
  const { data } = useLiveResource(activity.list, { query: {} }, {
    topic: "activity",
    seed: {
      groups: seed,
      scope: {
        environment: "real",
        category: null,
        q: "",
        available: true,
        unavailableReason: null,
      },
    },
    refreshInterval: 30_000,
  });
  const groups = data?.groups ?? seed;

  if (groups.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<CheckCheck strokeWidth={1.5} className="h-5 w-5" />}
          title="You&rsquo;re all caught up"
          description="Activity from across your workspace shows up here. As you and your agents do work, the latest events roll up into this feed."
        />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {groups.map((g) => (
        <ActivityGroupCard key={g.key} group={g} variant="page" />
      ))}
    </div>
  );
}
